import React from 'react';

import { useAppSelector } from '@/store';

import { parseSiteTranslations, siteTranslationsUrl, type SiteTranslations } from './site-translations';

// One read per table for the page's life: the tab remounts on a brand switch, and the table does not change in a visit.
const reads = new Map<string, Promise<SiteTranslations | null>>();
// The tables that came in, by where they were read: at hand from a reader's first render, so a reader mounted after
// its table came in (the tab opened again, or after the warm-up read it) names things in it at once.
const tables = new Map<string, SiteTranslations>();

const readTable = (url: string): Promise<SiteTranslations | null> => {
  const known = reads.get(url);
  if (known) return known;
  const read = fetch(url)
    .then((response) => (response.ok ? response.json() : null))
    .then((body: unknown) => (body === null ? null : parseSiteTranslations(body)))
    .catch(() => null);
  // A table that could not be read is asked again on the next visit, not taken as empty for good.
  void read.then((table) => {
    if (table === null) reads.delete(url);
    else tables.set(url, table);
  });
  reads.set(url, read);
  return read;
};

/** Where the brand's site's translation table for `language` is read, by the hub's config; null when it cannot say. */
const tableUrl = (tenantId: number | undefined, language: string): string | null =>
  siteTranslationsUrl(window.config as unknown as Record<string, unknown> | undefined, tenantId, language);

/** Starts reading the brand's site's translation table for `language`, for a reader to come (Game Lists names the
 *  site's pages in it): nothing when the config or the brand cannot say where it is, nor when it is read or being read.
 *  A read that fails is not kept, as a reader's is not. Never throws. */
export const warmSiteTranslations = (tenantId: number | undefined, language: string): void => {
  const url = tableUrl(tenantId, language);
  if (url) void readTable(url);
};

/** The brand's site's translation table for a language, and whether a reader has anything left to wait for. */
export interface SiteTable {
  /** The table, or null until it is read and when it cannot be: the texts then read as the sources give them. */
  table: SiteTranslations | null;
  /** Whether there is nothing left to wait for: the table is in, its read failed, or there is none to read (no language
   *  named, or a config or brand that cannot say where it is). False only while it is being read. */
  settled: boolean;
}

/** The brand's site's translation table for `language` (the site's own; '' while unknown). A table read before in the
 *  visit is there from the reader's first render, as it is once a language named later turns out to have one. */
export const useSiteTranslations = (language: string): SiteTable => {
  const brandTenant = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const tenantRaw = Number(brandTenant);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const url = tableUrl(tenantId, language);
  const [read, setRead] = React.useState<{ url: string; table: SiteTranslations | null } | null>(() => {
    const kept = url ? tables.get(url) : undefined;
    return url && kept ? { url, table: kept } : null;
  });
  React.useEffect(() => {
    if (!url) return undefined;
    let current = true;
    void readTable(url).then((table) => {
      if (current) setRead({ url, table });
    });
    return () => {
      current = false;
    };
  }, [url]);
  if (!url) return { table: null, settled: true };
  if (read?.url === url) return { table: read.table, settled: true };
  // The table of a language named after the first render (the site's, once the service names it) may be in already.
  const kept = tables.get(url);
  return kept ? { table: kept, settled: true } : { table: null, settled: false };
};
